import { useState, useRef, useEffect } from "react";
import { MessageSquare, Send, X, Minus, UserCircle, Bot } from "lucide-react";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

function genSessionId() {
  return "simone-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export default function LandingChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Olá! Eu sou a Simone, assistente virtual do Kaiso System. 👋\n\nPosso te ajudar com informações sobre preços, funcionalidades, demonstração ou como começar seu teste grátis de 29 dias!\n\nComo posso te ajudar hoje?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(genSessionId);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadContact, setLeadContact] = useState("");
  const [msgCount, setMsgCount] = useState(0);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip smooth scroll on first render to avoid "jumping" effect
    if (isFirstRender.current) {
      isFirstRender.current = false;
      endRef.current?.scrollIntoView({ behavior: "instant" });
    } else {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, showLeadForm]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    const newCount = msgCount + 1;
    setMsgCount(newCount);
    setMessages(prev => [...prev, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/simone/chat`, {
        message: text,
        session_id: sessionId,
      });
      setMessages(prev => [...prev, { role: "assistant", text: data.reply }]);
      // Show lead form after 3 user messages if not already submitted
      if (newCount >= 3 && !leadSent && !showLeadForm) {
        setShowLeadForm(true);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: "Desculpe, houve um erro. Tente novamente ou entre em contato pelo WhatsApp: +34 611 564 839" }]);
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async () => {
    if (!leadName.trim() || !leadContact.trim()) return;
    try {
      await axios.post(`${API}/api/simone/lead`, {
        name: leadName.trim(),
        contact: leadContact.trim(),
        session_id: sessionId,
        interest: messages.filter(m => m.role === "user").map(m => m.text).slice(-2).join(" | "),
      });
      setLeadSent(true);
      setShowLeadForm(false);
      setMessages(prev => [...prev, {
        role: "assistant",
        text: `Obrigada ${leadName.trim()}! 🎉\n\nNosso time comercial vai entrar em contato com você muito em breve.\n\nEnquanto isso, você pode começar seu teste grátis de 29 dias clicando em "Probar Gratis" no topo da página!`
      }]);
    } catch {
      // silently fail
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 bg-[#CDA34F] text-white pl-4 pr-5 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all group"
        data-testid="landing-chatbot-trigger"
      >
        <Bot className="w-5 h-5" />
        <span className="text-sm font-semibold">Simone IA</span>
        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white animate-pulse" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[370px] max-w-[calc(100vw-2rem)] flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden" data-testid="landing-chatbot-window" style={{ height: "520px" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#CDA34F] to-[#b8903e] text-white flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm font-bold leading-tight">Simone</p>
            <p className="text-[11px] opacity-80">IA do Kaiso System • Vendas & Suporte</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors" data-testid="chatbot-minimize">
            <Minus className="w-4 h-4" />
          </button>
          <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors" data-testid="chatbot-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50" data-testid="chatbot-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#CDA34F] text-white rounded-br-md"
                  : "bg-white text-gray-800 border border-gray-200 rounded-bl-md shadow-sm"
              }`}
              data-testid={`chat-msg-${m.role}-${i}`}
            >
              {m.text}
            </div>
          </div>
        ))}

        {/* Inline Lead Capture Form */}
        {showLeadForm && !leadSent && (
          <div className="flex justify-start" data-testid="lead-form-container">
            <div className="max-w-[90%] bg-white border-2 border-[#CDA34F]/30 rounded-2xl rounded-bl-md p-4 shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-[#CDA34F]">
                <UserCircle className="w-5 h-5" />
                <p className="text-sm font-semibold">¿Te gustaría recibir atención personalizada?</p>
              </div>
              <p className="text-xs text-gray-500">Déjanos tu nombre y contacto y nuestro equipo te atenderá.</p>
              <input
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
                placeholder="Tu nombre"
                className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#CDA34F]"
                data-testid="lead-name-input"
              />
              <input
                value={leadContact}
                onChange={(e) => setLeadContact(e.target.value)}
                placeholder="Email o WhatsApp"
                className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#CDA34F]"
                data-testid="lead-contact-input"
              />
              <div className="flex gap-2">
                <button
                  onClick={submitLead}
                  disabled={!leadName.trim() || !leadContact.trim()}
                  className="flex-1 text-sm py-2 bg-[#CDA34F] text-white rounded-lg font-semibold hover:bg-[#b8903e] transition-colors disabled:opacity-40"
                  data-testid="lead-submit-btn"
                >
                  Enviar
                </button>
                <button
                  onClick={() => setShowLeadForm(false)}
                  className="text-sm py-2 px-3 text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="lead-dismiss-btn"
                >
                  Ahora no
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="px-3 py-2.5 border-t border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Escribe tu pregunta..."
            className="flex-1 text-sm px-3.5 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-[#CDA34F] focus:ring-1 focus:ring-[#CDA34F]/30 bg-gray-50"
            disabled={loading}
            data-testid="chatbot-input"
          />
          <button
            onClick={send}
            disabled={!input.trim() || loading}
            className="p-2.5 bg-[#CDA34F] text-white rounded-xl hover:bg-[#b8903e] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
            data-testid="chatbot-send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
